import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { feedbackApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  MessageSquare,
  Loader2,
  Search,
  Filter,
  CheckCircle,
  Clock,
  AlertCircle,
  Lightbulb,
  Bug,
  MessageCircle,
  Building2,
  User,
  Mail,
  Calendar,
  Send,
  Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const FeedbacksPage = () => {
  const { user, isSuperAdmin } = useAuth();
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('todos');
  const [filterType, setFilterType] = useState('todos');
  const [filterCompany, setFilterCompany] = useState('todos');
  
  // Response dialog
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showResponseDialog, setShowResponseDialog] = useState(false);
  const [responseText, setResponseText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadFeedbacks();
  }, []);

  const loadFeedbacks = async () => {
    try {
      const res = await feedbackApi.list();
      setFeedbacks(res.data || []);
    } catch (error) {
      console.error('Error loading feedbacks:', error);
      toast.error('Erro ao carregar feedbacks');
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'sugestao':
        return <Lightbulb className="w-4 h-4 text-amber-500" />;
      case 'bug':
        return <Bug className="w-4 h-4 text-red-500" />;
      default:
        return <MessageCircle className="w-4 h-4 text-blue-500" />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'sugestao':
        return 'Sugestão';
      case 'bug':
        return 'Bug';
      default:
        return 'Outro';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pendente':
        return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200"><Clock className="w-3 h-3 mr-1" /> Pendente</Badge>;
      case 'lido':
        return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200"><Eye className="w-3 h-3 mr-1" /> Lido</Badge>;
      case 'respondido':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200"><CheckCircle className="w-3 h-3 mr-1" /> Respondido</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleMarkAsRead = async (feedback) => {
    try {
      await feedbackApi.update(feedback.id, { status: 'lido' });
      toast.success('Marcado como lido');
      loadFeedbacks();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const openResponseDialog = (feedback) => {
    setSelectedFeedback(feedback);
    setResponseText(feedback.admin_response || '');
    setShowResponseDialog(true);
  };

  const handleSendResponse = async () => {
    if (!responseText.trim()) {
      toast.error('Digite uma resposta');
      return;
    }
    
    setSaving(true);
    try {
      await feedbackApi.update(selectedFeedback.id, {
        status: 'respondido',
        admin_response: responseText
      });
      toast.success('Resposta salva com sucesso');
      setShowResponseDialog(false);
      loadFeedbacks();
    } catch (error) {
      toast.error('Erro ao salvar resposta');
    } finally {
      setSaving(false);
    }
  };

  // Get unique companies for filter
  const companies = [...new Set(feedbacks.map(f => f.company_name).filter(Boolean))];

  // Filter feedbacks
  const filteredFeedbacks = feedbacks.filter(f => {
    if (filterStatus !== 'todos' && f.status !== filterStatus) return false;
    if (filterType !== 'todos' && f.type !== filterType) return false;
    if (filterCompany !== 'todos' && f.company_name !== filterCompany) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        f.message?.toLowerCase().includes(search) ||
        f.user_name?.toLowerCase().includes(search) ||
        f.user_email?.toLowerCase().includes(search) ||
        f.company_name?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  // Stats
  const stats = {
    total: feedbacks.length,
    pendentes: feedbacks.filter(f => f.status === 'pendente').length,
    lidos: feedbacks.filter(f => f.status === 'lido').length,
    respondidos: feedbacks.filter(f => f.status === 'respondido').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-amber-500" />
            Feedbacks e Sugestões
          </h1>
          <p className="text-slate-500 mt-1">
            {isSuperAdmin() ? 'Visualize feedbacks de todas as empresas' : 'Visualize feedbacks da sua empresa'}
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              </div>
              <MessageSquare className="w-8 h-8 text-slate-300" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600">Pendentes</p>
                <p className="text-2xl font-bold text-amber-600">{stats.pendentes}</p>
              </div>
              <Clock className="w-8 h-8 text-amber-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600">Lidos</p>
                <p className="text-2xl font-bold text-blue-600">{stats.lidos}</p>
              </div>
              <Eye className="w-8 h-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Respondidos</p>
                <p className="text-2xl font-bold text-green-600">{stats.respondidos}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por mensagem, usuário ou empresa..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos Status</SelectItem>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="lido">Lido</SelectItem>
                  <SelectItem value="respondido">Respondido</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos Tipos</SelectItem>
                  <SelectItem value="sugestao">Sugestão</SelectItem>
                  <SelectItem value="bug">Bug</SelectItem>
                  <SelectItem value="outro">Outro</SelectItem>
                </SelectContent>
              </Select>
              {isSuperAdmin() && companies.length > 0 && (
                <Select value={filterCompany} onValueChange={setFilterCompany}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Empresa" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todas Empresas</SelectItem>
                    {companies.map(company => (
                      <SelectItem key={company} value={company}>{company}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Feedbacks List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {filteredFeedbacks.length} feedback{filteredFeedbacks.length !== 1 ? 's' : ''} encontrado{filteredFeedbacks.length !== 1 ? 's' : ''}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredFeedbacks.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>Nenhum feedback encontrado</p>
            </div>
          ) : (
            <ScrollArea className="h-[500px]">
              <div className="space-y-4">
                {filteredFeedbacks.map((feedback) => (
                  <div
                    key={feedback.id}
                    className={`p-4 rounded-lg border transition-colors ${
                      feedback.status === 'pendente' 
                        ? 'bg-amber-50/50 border-amber-200' 
                        : 'bg-white border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="flex-1">
                        {/* Header */}
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          {getTypeIcon(feedback.type)}
                          <span className="text-sm font-medium text-slate-700">
                            {getTypeLabel(feedback.type)}
                          </span>
                          {getStatusBadge(feedback.status)}
                          {isSuperAdmin() && feedback.company_name && (
                            <Badge variant="outline" className="bg-slate-50">
                              <Building2 className="w-3 h-3 mr-1" />
                              {feedback.company_name}
                            </Badge>
                          )}
                        </div>
                        
                        {/* Message */}
                        <p className="text-slate-800 mb-3 whitespace-pre-wrap">
                          {feedback.message}
                        </p>
                        
                        {/* Admin Response */}
                        {feedback.admin_response && (
                          <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                            <p className="text-xs font-medium text-green-700 mb-1">Resposta do Admin:</p>
                            <p className="text-sm text-green-800">{feedback.admin_response}</p>
                          </div>
                        )}
                        
                        {/* Meta info */}
                        <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 mt-3">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {feedback.user_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {feedback.user_email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {format(new Date(feedback.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                          </span>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex gap-2">
                        {feedback.status === 'pendente' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleMarkAsRead(feedback)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Marcar como Lido
                          </Button>
                        )}
                        <Button
                          variant={feedback.status === 'respondido' ? 'outline' : 'default'}
                          size="sm"
                          onClick={() => openResponseDialog(feedback)}
                          className={feedback.status !== 'respondido' ? 'bg-amber-500 hover:bg-amber-600' : ''}
                        >
                          <Send className="w-4 h-4 mr-1" />
                          {feedback.status === 'respondido' ? 'Ver/Editar Resposta' : 'Responder'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Response Dialog */}
      <Dialog open={showResponseDialog} onOpenChange={setShowResponseDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Responder Feedback</DialogTitle>
            <DialogDescription>
              Escreva uma resposta para o usuário
            </DialogDescription>
          </DialogHeader>
          {selectedFeedback && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500 mb-1">Mensagem do usuário:</p>
                <p className="text-sm text-slate-700">{selectedFeedback.message}</p>
                <p className="text-xs text-slate-400 mt-2">
                  De: {selectedFeedback.user_name} ({selectedFeedback.user_email})
                </p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Sua resposta</label>
                <Textarea
                  placeholder="Digite sua resposta..."
                  value={responseText}
                  onChange={(e) => setResponseText(e.target.value)}
                  rows={4}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResponseDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleSendResponse}
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Salvar Resposta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeedbacksPage;
